import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { toast } from "sonner";
import {
  Plus, Users, Send, AlertTriangle, ImageIcon,
  X, CheckCircle2, Calendar, Megaphone,
} from "lucide-react";

const EVENT_SEGMENTS = [
  { value: "All", label: "All Customers", desc: "Every opted-in customer" },
  { value: "Regular", label: "Regulars & New", desc: "Visited in last 30 days" },
  { value: "New", label: "New Only", desc: "Joined in last 7 days" },
  { value: "LostAtRisk", label: "Lost & At Risk", desc: "Last visit 15+ days ago — re-engage" },
];

function typeBadge(type) {
  if (type === "event") return "bg-[#9D00FF]/12 text-[#D78CFF] border-[#9D00FF]/40";
  if (type === "winback") return "bg-[#F59E0B]/12 text-[#F59E0B] border-[#F59E0B]/40";
  return "bg-white/10 text-[#A0A0A0] border-white/20";
}

function typeLabel(type) {
  if (type === "event") return "Event";
  if (type === "winback") return "Win-back";
  return type;
}

function formatCampaignDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function Promotions() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [atRiskCount, setAtRiskCount] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState("type"); // "type" | "event" | "winback"

  // Event campaign state
  const [eventImageUrl, setEventImageUrl] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const [eventImageUploading, setEventImageUploading] = useState(false);
  const [eventMessage, setEventMessage] = useState("");
  const [eventSegment, setEventSegment] = useState("All");
  const [eventReach, setEventReach] = useState(null);
  const [eventReachLoading, setEventReachLoading] = useState(false);
  const [eventSending, setEventSending] = useState(false);
  const [eventCampaignId, setEventCampaignId] = useState(() => {
    // Restore from sessionStorage so progress bar survives tab switches
    try { return sessionStorage.getItem("tabon_active_campaign_id") || null; }
    catch { return null; }
  });
  const [eventProgress, setEventProgress] = useState(null);

  // Schedule state
  const [useSchedule, setUseSchedule] = useState(false);
  const [sendAfter, setSendAfter] = useState("");
  // Event/offer deadline — we stop delivering after this time
  const [eventTime, setEventTime] = useState("");

  // Win-back state
  const [winbackSending, setWinbackSending] = useState(false);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch {
      // silent
    } finally {
      setLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
    api.get("/dashboard/stats")
      .then(({ data }) => setAtRiskCount(data.at_risk ?? null))
      .catch(() => {});
  }, []);

  // Reach estimate
  useEffect(() => {
    if (!showModal || modalStep !== "event") return;
    let cancelled = false;
    setEventReach(null);
    setEventReachLoading(true);
    api.get("/campaigns/event/reach", { params: { segment: eventSegment } })
      .then(({ data }) => { if (!cancelled) setEventReach(data.reach); })
      .catch(() => { if (!cancelled) setEventReach(null); })
      .finally(() => { if (!cancelled) setEventReachLoading(false); });
    return () => { cancelled = true; };
  }, [eventSegment, showModal, modalStep]);

  // Poll campaign progress every 5 seconds
  // Survives tab switches (campaign ID restored from sessionStorage on mount)
  // Updates campaign card live every 30 seconds via loadCampaigns()
  // Network errors are ignored — polling continues
  // Stops cleanly on "completed" or "failed" status
  useEffect(() => {
    if (!eventCampaignId) return;
    let pollCount = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/campaigns/event/${eventCampaignId}/status`);
        setEventProgress(data);
        pollCount++;

        // Refresh campaign card every 30 seconds (every 6 polls at 5s interval)
        // so "0 delivered" updates to real numbers while sending
        if (pollCount % 6 === 0) {
          loadCampaigns();
        }

        if (data.status === "completed") {
          clearInterval(pollRef.current);
          try { sessionStorage.removeItem("tabon_active_campaign_id"); } catch {}
          setEventCampaignId(null);
          setEventProgress(null);
          loadCampaigns();
          toast.success(
            `Campaign complete — ${data.sent} sent, ${data.skipped} skipped` +
            (data.failed > 0 ? `, ${data.failed} undelivered` : "")
          );
        }

        if (data.status === "failed") {
          clearInterval(pollRef.current);
          try { sessionStorage.removeItem("tabon_active_campaign_id"); } catch {}
          setEventCampaignId(null);
          loadCampaigns();
          toast(`Campaign paused after ${data.sent} messages. ` +
            `${(data.total || 0) - (data.sent || 0) - (data.failed || 0) - (data.skipped || 0)} customers haven't received this message yet.`
          );
        }

      } catch {
        // Network error — do NOT clear campaign ID or stop polling
        // The campaign is still running on the server, we just missed one poll
        // polling will retry on the next 5-second cycle
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [eventCampaignId]);

  const openModal = () => {
    setModalStep("type");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalStep("type");
  };

  const resetEventState = () => {
    setEventImageUrl(null);
    setEventImagePreview(null);
    setEventMessage("");
    setEventSegment("All");
    setUseSchedule(false);
    setSendAfter("");
    setEventTime("");
    setEventSending(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      toast.error("Only JPEG or PNG accepted"); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large — max 5MB"); return;
    }
    setEventImagePreview(URL.createObjectURL(file));
    setEventImageUrl(null);
    setEventImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/campaigns/event/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEventImageUrl(data.image_url);
      toast.success(`Poster uploaded (${data.size_kb} KB)`);
    } catch (err) {
      toast.error(formatError(err));
      setEventImagePreview(null);
      setEventImageUrl(null);
    } finally {
      setEventImageUploading(false);
    }
  };

  const clearImage = () => {
    setEventImageUrl(null);
    setEventImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const sendEventCampaign = async () => {
    if (!eventImageUrl) { toast.error("Upload an event poster first"); return; }
    if (!eventMessage.trim()) { toast.error("Enter a message"); return; }
    if (eventMessage.length > 1024) { toast.error("Message exceeds 1024 characters"); return; }
    if (useSchedule && !sendAfter) { toast.error("Enter a schedule time"); return; }
    if (useSchedule && new Date(sendAfter) <= new Date()) {
      toast.error("Scheduled time must be in the future"); return;
    }
    setEventSending(true);
    try {
      const { data } = await api.post("/campaigns/event", {
        image_url: eventImageUrl,
        message_text: eventMessage.trim(),
        target_segment: eventSegment,
        send_after: useSchedule && sendAfter
          ? new Date(sendAfter).toISOString()
          : null,
        event_time: eventTime ? new Date(eventTime).toISOString() : null,
      });
      if (!data.ok) {
        toast.error(data.message || "No eligible customers");
        setEventSending(false);
        return;
      }
      closeModal();
      resetEventState();
      loadCampaigns();
      if (data.scheduled) {
        toast.success(data.message);
      } else {
        try { sessionStorage.setItem("tabon_active_campaign_id", data.campaign_id); } catch {}
        setEventCampaignId(data.campaign_id);
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(formatError(err));
      setEventSending(false);
    }
  };

  const sendWinback = async () => {
    setWinbackSending(true);
    try {
      await api.post("/campaigns/winback", { target_segment: "At Risk" });
      toast.success("Win-back campaign sent");
      closeModal();
      loadCampaigns();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setWinbackSending(false);
    }
  };

  const filteredCampaigns = campaigns.filter(c =>
    campaignFilter === "all" || c.type === campaignFilter
  );

  const progressPct = eventProgress && eventProgress.total > 0
    ? Math.round(((eventProgress.sent + eventProgress.failed + eventProgress.skipped) / eventProgress.total) * 100)
    : 0;

  return (
    <AppLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 flex flex-col gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Campaigns</h1>
          <p className="text-sm text-[#A0A0A0]">All customer outreach — event campaigns, win-back, and more</p>
        </div>

        {/* ── Campaign History ── */}
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Megaphone size={18} className="text-[#9D00FF]" />
              <span className="text-base font-bold text-white">All campaigns</span>
              {campaigns.length > 0 && (
                <span className="text-xs text-[#A0A0A0]">{campaigns.length} total</span>
              )}
            </div>
            <button
              type="button"
              onClick={openModal}
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white font-semibold rounded-lg px-4 py-2 flex items-center gap-2 text-sm min-h-[40px] transition"
            >
              <Plus size={15} /> New campaign
            </button>
          </div>

          {/* Filter pills */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "all", label: "All" },
              { key: "event", label: "Event" },
              { key: "winback", label: "Win-back" },
            ].map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => setCampaignFilter(f.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  campaignFilter === f.key
                    ? "bg-[#9D00FF]/15 border-[#9D00FF] text-[#D78CFF]"
                    : "border-white/10 text-[#A0A0A0] hover:border-white/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Active send progress bar — only shown while sending */}
          {eventCampaignId && eventProgress && eventProgress.status !== "failed" && (
            <div className="bg-[#0A0A0A] border border-[#9D00FF]/30 rounded-lg px-4 py-3 flex flex-col gap-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#9D00FF] font-semibold">Campaign sending…</span>
                <span className="text-[#A0A0A0]">
                  {(eventProgress.sent || 0) + (eventProgress.failed || 0) + (eventProgress.skipped || 0)} / {eventProgress.total}
                </span>
              </div>
              <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div
                  className="h-1.5 bg-[#9D00FF] rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Campaign list */}
          {loadingCampaigns ? (
            <div className="text-sm text-[#A0A0A0] py-6 text-center">Loading campaigns…</div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <Megaphone size={28} className="mx-auto text-[#9D00FF] mb-2 opacity-50" />
              <div className="text-sm font-semibold text-white">No campaigns yet</div>
              <p className="text-xs text-[#A0A0A0] mt-1">Click "New campaign" to send your first one</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-white/5">
              {filteredCampaigns.map((c) => (
                <div key={c.id} className="py-3 flex items-start gap-3 first:pt-0 last:pb-0">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden border border-white/10 bg-[#0A0A0A] flex items-center justify-center">
                    {c.type === "event" && c.image_url ? (
                      <img src={c.image_url} alt="" className="w-full h-full object-cover" />
                    ) : c.type === "event" ? (
                      <ImageIcon size={16} className="text-[#9D00FF]" />
                    ) : (
                      <Users size={16} className="text-[#F59E0B]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate mb-1">
                      {c.type === "event"
                        ? (c.message_text?.slice(0, 70) + (c.message_text?.length > 70 ? "…" : ""))
                        : "Win-back re-engagement"}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase tracking-wide ${typeBadge(c.type)}`}>
                        {typeLabel(c.type)}
                      </span>
                      {c.target_segment && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-[#A0A0A0] bg-white/5">
                          {c.target_segment === "All" ? "All customers" : c.target_segment === "Regular" ? "Regulars & new" : c.target_segment}
                        </span>
                      )}
                      <span className="text-[10px] text-[#A0A0A0]">
                        {c.status === "scheduled" && c.send_after
                          ? `Scheduled: ${formatCampaignDate(c.send_after)}`
                          : formatCampaignDate(c.created_at)}
                      </span>
                    </div>
                    {c.status !== "scheduled" && (
                      <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                        <span className="text-emerald-400 font-semibold">{c.sent ?? 0} sent</span>
                        {(c.failed ?? 0) > 0 && <span className="text-[#A0A0A0]">{c.failed} failed</span>}
                        {(c.skipped ?? 0) > 0 && <span className="text-[#A0A0A0]">{c.skipped} skipped</span>}
                        <span className="text-[#A0A0A0]">{c.total} targeted</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── New Campaign Modal ── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto tb-fade-up">
            <div className="flex items-center justify-between px-5 pt-5 pb-0 mb-4">
              <h2 className="text-lg font-bold text-white">
                {modalStep === "type" ? "New campaign" : modalStep === "event" ? "Event campaign" : "Win-back campaign"}
              </h2>
              <button type="button" onClick={closeModal} className="text-[#A0A0A0] hover:text-white p-1">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 pb-5">
              {/* Step 1 — Type picker */}
              {modalStep === "type" && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setModalStep("event")}
                    className="text-left p-4 rounded-xl border border-white/10 hover:border-[#9D00FF]/60 hover:bg-[#9D00FF]/5 transition flex flex-col gap-2"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#9D00FF]/15 border border-[#9D00FF]/30 flex items-center justify-center">
                      <ImageIcon size={18} className="text-[#9D00FF]" />
                    </div>
                    <span className="text-sm font-bold text-white">Event campaign</span>
                    <span className="text-xs text-[#A0A0A0] leading-relaxed">Share an event poster with a custom message to your customers</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalStep("winback")}
                    className="text-left p-4 rounded-xl border border-white/10 hover:border-[#F59E0B]/60 hover:bg-[#F59E0B]/5 transition flex flex-col gap-2"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                      <Users size={18} className="text-[#F59E0B]" />
                    </div>
                    <span className="text-sm font-bold text-white">Win-back</span>
                    <span className="text-xs text-[#A0A0A0] leading-relaxed">Re-engage customers who haven't visited in 30+ days</span>
                  </button>
                </div>
              )}

              {/* Step 2a — Event compose */}
              {modalStep === "event" && (
                <div className="flex flex-col gap-4">
                  {/* Image upload */}
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Event poster</label>
                    {eventImagePreview ? (
                      <div className="relative inline-block">
                        <img src={eventImagePreview} alt="Preview" className="h-40 w-auto rounded-lg object-contain border border-white/10" />
                        {eventImageUploading && (
                          <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                            <span className="text-xs text-white animate-pulse">Uploading…</span>
                          </div>
                        )}
                        {!eventImageUploading && eventImageUrl && (
                          <div className="absolute top-2 right-2 bg-emerald-500 rounded-full p-0.5">
                            <CheckCircle2 size={12} className="text-white" />
                          </div>
                        )}
                        <button type="button" onClick={clearImage} className="absolute top-2 left-2 bg-black/70 rounded-full p-1">
                          <X size={11} className="text-white" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border border-dashed border-white/20 rounded-xl p-6 flex flex-col items-center gap-2 hover:border-[#9D00FF]/50 hover:bg-[#9D00FF]/5 transition"
                      >
                        <ImageIcon size={24} className="text-[#A0A0A0]" />
                        <span className="text-sm text-[#A0A0A0]">Click to upload event poster</span>
                        <span className="text-xs text-[#A0A0A0]">JPEG or PNG · Max 5MB</span>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png" className="hidden" onChange={handleImageSelect} />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Message</label>
                    <textarea
                      value={eventMessage}
                      onChange={(e) => setEventMessage(e.target.value)}
                      rows={3}
                      maxLength={1024}
                      placeholder="e.g. Sufi Night this Saturday at 7:30 PM! Cover charges apply. Book your table: 8796915550"
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF] focus:ring-1 focus:ring-[#9D00FF]/30 resize-none"
                    />
                    <div className="flex items-start justify-between gap-3 mt-1">
                      {/* WhatsApp collapses every newline to a space on send (_wa_param_text), so a
                          message typed across lines arrives as one paragraph — say so up front. */}
                      <p className="text-xs text-[#A0A0A0]">
                        Line breaks aren't preserved — WhatsApp sends this as one continuous paragraph.
                      </p>
                      <span className={`text-xs whitespace-nowrap ${eventMessage.length > 900 ? "text-[#F59E0B]" : "text-[#A0A0A0]"}`}>
                        {eventMessage.length}/1024
                      </span>
                    </div>
                  </div>

                  {/* Segment */}
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-2 block">Send to</label>
                    <div className="grid grid-cols-2 gap-2">
                      {EVENT_SEGMENTS.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => setEventSegment(s.value)}
                          className={`text-left p-2.5 rounded-lg border text-xs transition ${
                            eventSegment === s.value
                              ? "bg-[#9D00FF]/15 border-[#9D00FF] text-white"
                              : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0] hover:border-[#9D00FF]/40"
                          }`}
                        >
                          <span className="font-semibold block">{s.label}</span>
                          <span className="opacity-70 leading-tight block mt-0.5">{s.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reach */}
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 flex items-center justify-between text-sm">
                    <span className="text-[#A0A0A0]">Estimated reach</span>
                    <span className="font-bold text-white">
                      {eventReachLoading ? "Calculating…" : eventReach !== null ? `~${eventReach} customers` : "—"}
                    </span>
                  </div>

                  {/* Schedule toggle */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                      <input
                        type="checkbox"
                        checked={useSchedule}
                        onChange={(e) => { setUseSchedule(e.target.checked); if (!e.target.checked) setSendAfter(""); }}
                        className="accent-[#9D00FF] w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-white flex items-center gap-1.5">
                        <Calendar size={14} className="text-[#9D00FF]" /> Schedule for later (IST)
                      </span>
                    </label>
                    {useSchedule && (
                      <>
                        <input
                          type="datetime-local"
                          value={sendAfter}
                          onChange={(e) => setSendAfter(e.target.value)}
                          className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF] min-h-[44px]"
                        />
                        <p className="text-[11px] text-[#A0A0A0] mt-1">
                          Campaign will start sending at this time. All times are in IST.
                        </p>
                      </>
                    )}
                  </div>

                  {/* Event / offer deadline */}
                  <div>
                    <label className="text-sm font-semibold text-white flex items-center gap-1.5 mb-2">
                      <Calendar size={14} className="text-[#9D00FF]" /> When is your event / offer? (IST)
                    </label>
                    <input
                      type="datetime-local"
                      value={eventTime}
                      onChange={(e) => setEventTime(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D00FF] min-h-[44px]"
                    />
                    <p className="text-[11px] text-[#A0A0A0] mt-1">
                      We'll stop sending after this time so no one gets it too late. Leave blank for a default 24-hour window.
                    </p>
                  </div>

                  {/* Send-earlier guidance — non-blocking */}
                  {eventTime
                    && (new Date(eventTime).getTime() - Date.now()) < 24 * 60 * 60 * 1000
                    && (new Date(eventTime).getTime() - Date.now()) > 0
                    && (eventReach ?? 0) > 100 && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5 text-[11px] text-amber-300 leading-relaxed">
                      Sending close to your event may reduce reach — WhatsApp limits how many promotions a customer receives. Send 2–3 days ahead for best results.
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button type="button" onClick={() => setModalStep("type")} className="border border-white/10 text-[#A0A0A0] hover:text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition">
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={sendEventCampaign}
                      disabled={eventSending || eventImageUploading || !eventImageUrl || !eventMessage.trim()}
                      className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 text-sm min-h-[44px] transition"
                    >
                      <Send size={14} />
                      {eventSending ? "Starting…" : useSchedule ? "Schedule campaign" : "Send campaign"}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2b — Win-back confirm */}
              {modalStep === "winback" && (
                <div className="flex flex-col gap-4">
                  <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-sm text-[#A0A0A0] leading-relaxed">
                    <div className="font-semibold text-white mb-1 flex items-center gap-2">
                      <Users size={14} className="text-[#F59E0B]" /> Re-engage at-risk customers
                    </div>
                    A WhatsApp message will be sent to customers who haven't visited in 30+ days. No image or composition needed — the message is pre-configured. Customers messaged in the last 7 days are automatically excluded.
                  </div>
                  {atRiskCount !== null && (
                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle size={14} className="text-[#F59E0B]" />
                      <span className="text-white font-semibold">{atRiskCount}</span>
                      <span className="text-[#A0A0A0]">customers currently at risk</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setModalStep("type")} className="border border-white/10 text-[#A0A0A0] hover:text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition">
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={sendWinback}
                      disabled={winbackSending || atRiskCount === 0}
                      className="flex-1 tb-glow bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-50 text-black font-semibold rounded-lg px-4 py-2.5 flex items-center justify-center gap-2 text-sm min-h-[44px] transition"
                    >
                      <Send size={14} />
                      {winbackSending ? "Sending…" : "Send win-back message"}
                    </button>
                  </div>
                  {atRiskCount === 0 && (
                    <p className="text-xs text-[#A0A0A0]">No at-risk customers right now — check back later.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
